// 인쇄용 고해상도 QR 코드 다운로드 유틸리티
// 미리보기(QRCode.tsx)와 동일한 QR API를 사용하되, 1000px PNG / 벡터 SVG로 받아
// 로고를 실제 이미지에 합성한 뒤 파일로 저장한다.

export interface QRDownloadOptions {
  value: string;
  color?: string;
  backgroundColor?: string;
  logoUrl?: string | null;
  filename?: string;
}

export interface QRDownloadResult {
  // 로고가 지정됐지만 CORS 등의 이유로 합성하지 못한 경우 false
  logoIncluded: boolean;
}

// api.qrserver.com 최대 출력 크기
const QR_API_SIZE = 1000;

// 미리보기(QRCode.tsx: size 256, logoSize 80, margin 8, radius 8)와 동일한 비율
const LOGO_RATIO = 80 / 256;
const LOGO_MARGIN_RATIO = 8 / 256;
const LOGO_RADIUS_RATIO = 8 / 256;

const buildQrApiUrl = (options: QRDownloadOptions, size: number, format?: 'svg') => {
  const color = (options.color || '#000000').replace('#', '');
  const bgcolor = (options.backgroundColor || '#FFFFFF').replace('#', '');
  // qzone=2: 인쇄/스캔 안정성을 위한 여백(quiet zone)
  let url = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(options.value)}&color=${color}&bgcolor=${bgcolor}&ecc=H&qzone=2`;
  if (format) url += `&format=${format}`;
  return url;
};

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src.slice(0, 100)}`));
    img.src = src;
  });

const fetchAsDataURL = async (url: string): Promise<string> => {
  if (url.startsWith('data:')) return url;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read blob'));
    reader.readAsDataURL(blob);
  });
};

const drawRoundedRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const sanitizeFilename = (name: string) =>
  name.replace(/[\\/:*?"<>|\s]+/g, '-').replace(/^-+|-+$/g, '') || 'qr-code';

/**
 * 고해상도 PNG 다운로드 (1000x1000, 명함 기준 약 1200 DPI)
 */
export const downloadQRPng = async (options: QRDownloadOptions): Promise<QRDownloadResult> => {
  const qrImage = await loadImage(buildQrApiUrl(options, QR_API_SIZE));
  // qzone(여백) 때문에 API가 요청 크기보다 큰 이미지를 반환할 수 있으므로
  // 원본 크기 그대로 사용해 리샘플링으로 인한 모듈 경계 블러를 방지한다
  const size = qrImage.naturalWidth || QR_API_SIZE;

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');

  ctx.fillStyle = options.backgroundColor || '#FFFFFF';
  ctx.fillRect(0, 0, size, size);
  ctx.drawImage(qrImage, 0, 0, size, size);

  let logoIncluded = true;
  if (options.logoUrl) {
    logoIncluded = false;
    try {
      const logoDataUrl = await fetchAsDataURL(options.logoUrl);
      const logoImage = await loadImage(logoDataUrl);

      const logoSize = size * LOGO_RATIO;
      const margin = size * LOGO_MARGIN_RATIO;
      const radius = size * LOGO_RADIUS_RATIO;
      const boxSize = logoSize + margin * 2;
      const boxX = (size - boxSize) / 2;
      const boxY = (size - boxSize) / 2;

      // 로고 뒤 흰색 라운드 배경
      ctx.fillStyle = '#FFFFFF';
      drawRoundedRect(ctx, boxX, boxY, boxSize, boxSize, radius);
      ctx.fill();

      // 로고 (object-fit: cover 방식으로 중앙 크롭)
      const scale = Math.max(logoSize / logoImage.width, logoSize / logoImage.height);
      const sw = logoSize / scale;
      const sh = logoSize / scale;
      const sx = (logoImage.width - sw) / 2;
      const sy = (logoImage.height - sh) / 2;

      ctx.save();
      drawRoundedRect(ctx, boxX + margin, boxY + margin, logoSize, logoSize, Math.max(0, radius - margin));
      ctx.clip();
      ctx.drawImage(logoImage, sx, sy, sw, sh, boxX + margin, boxY + margin, logoSize, logoSize);
      ctx.restore();

      logoIncluded = true;
    } catch (error) {
      console.error('[qrDownload] 로고 합성 실패, 로고 없이 다운로드:', error);
    }
  }

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Failed to export PNG'))), 'image/png');
  });

  triggerDownload(blob, `${sanitizeFilename(options.filename || 'qr-code')}.png`);
  return { logoIncluded };
};

/**
 * 벡터 SVG 다운로드 (인쇄소 제출용 — 크기 제한 없이 선명)
 */
export const downloadQRSvg = async (options: QRDownloadOptions): Promise<QRDownloadResult> => {
  const response = await fetch(buildQrApiUrl(options, QR_API_SIZE, 'svg'));
  if (!response.ok) throw new Error(`Failed to fetch SVG: ${response.status}`);
  let svgText = await response.text();

  let logoIncluded = true;
  if (options.logoUrl) {
    logoIncluded = false;
    try {
      const logoDataUrl = await fetchAsDataURL(options.logoUrl);

      const parser = new DOMParser();
      const doc = parser.parseFromString(svgText, 'image/svg+xml');
      const svg = doc.documentElement;
      if (svg.nodeName !== 'svg') throw new Error('Invalid SVG');

      // 좌표계 크기 파악 (viewBox 우선, 없으면 width/height 속성)
      let vbWidth = 0;
      let vbHeight = 0;
      let vbX = 0;
      let vbY = 0;
      const viewBox = svg.getAttribute('viewBox');
      if (viewBox) {
        const parts = viewBox.trim().split(/[\s,]+/).map(Number);
        if (parts.length === 4 && parts.every((n) => !isNaN(n))) {
          [vbX, vbY, vbWidth, vbHeight] = parts;
        }
      }
      if (!vbWidth || !vbHeight) {
        vbWidth = parseFloat(svg.getAttribute('width') || '') || QR_API_SIZE;
        vbHeight = parseFloat(svg.getAttribute('height') || '') || QR_API_SIZE;
      }

      const base = Math.min(vbWidth, vbHeight);
      const logoSize = base * LOGO_RATIO;
      const margin = base * LOGO_MARGIN_RATIO;
      const radius = base * LOGO_RADIUS_RATIO;
      const boxSize = logoSize + margin * 2;
      const boxX = vbX + (vbWidth - boxSize) / 2;
      const boxY = vbY + (vbHeight - boxSize) / 2;

      const SVG_NS = 'http://www.w3.org/2000/svg';

      const rect = doc.createElementNS(SVG_NS, 'rect');
      rect.setAttribute('x', String(boxX));
      rect.setAttribute('y', String(boxY));
      rect.setAttribute('width', String(boxSize));
      rect.setAttribute('height', String(boxSize));
      rect.setAttribute('rx', String(radius));
      rect.setAttribute('fill', '#FFFFFF');
      svg.appendChild(rect);

      const image = doc.createElementNS(SVG_NS, 'image');
      image.setAttribute('x', String(boxX + margin));
      image.setAttribute('y', String(boxY + margin));
      image.setAttribute('width', String(logoSize));
      image.setAttribute('height', String(logoSize));
      image.setAttribute('preserveAspectRatio', 'xMidYMid slice');
      image.setAttribute('href', logoDataUrl);
      image.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', logoDataUrl);
      svg.appendChild(image);

      svgText = new XMLSerializer().serializeToString(svg);
      logoIncluded = true;
    } catch (error) {
      console.error('[qrDownload] SVG 로고 합성 실패, 로고 없이 다운로드:', error);
    }
  }

  const blob = new Blob([svgText], { type: 'image/svg+xml' });
  triggerDownload(blob, `${sanitizeFilename(options.filename || 'qr-code')}.svg`);
  return { logoIncluded };
};
